(() => {
  'use strict';

  const priceForCut = (rate, metres, standardMetres, standardPrice) => {
    if (Math.abs(metres - standardMetres) < 0.000001) return standardPrice;
    return Math.ceil((rate * metres) / 5) * 5;
  };

  const quote = ({ rate, kameezCount, waistcoatCount, kameezMetres, waistcoatMetres,
    kameezStandard, waistcoatStandard, kameezPrice, waistcoatPrice }) => {
    const counts = [kameezCount, waistcoatCount];
    if (!Number.isFinite(rate) || rate <= 0 || counts.some((n) => !Number.isInteger(n) || n < 0 || n > 999)) {
      return { valid: false, error: 'Enter a whole-number quantity between 0 and 999 for each piece.' };
    }
    const lengths = [kameezMetres, waistcoatMetres];
    if (lengths.some((n) => !Number.isFinite(n) || n < 0.01 || n > 100 || Math.abs(n * 100 - Math.round(n * 100)) > 0.000001)) {
      return { valid: false, error: 'Enter a cut length from 0.01 m to 100 m, using no more than two decimal places.' };
    }
    if (kameezCount + waistcoatCount === 0) return { valid: false, error: 'Choose a quantity for at least one piece.' };
    const kameezUnit = priceForCut(rate, kameezMetres, kameezStandard, kameezPrice);
    const waistcoatUnit = priceForCut(rate, waistcoatMetres, waistcoatStandard, waistcoatPrice);
    return {
      valid: true,
      kameezUnit,
      waistcoatUnit,
      kameezTotal: kameezUnit * kameezCount,
      waistcoatTotal: waistcoatUnit * waistcoatCount,
      total: kameezUnit * kameezCount + waistcoatUnit * waistcoatCount,
    };
  };

  const formatMetres = (metres) => `${Number(metres.toFixed(2))} m`;
  const formatPkr = (amount) => `PKR ${amount.toLocaleString('en')}`;
  const buildMessage = ({ quality, lot, kameezShade, waistcoatShade, kameezCount, waistcoatCount,
    kameezMetres, waistcoatMetres, kameezTotal, waistcoatTotal, total }) => {
    const pieces = [];
    if (kameezCount) pieces.push(`Shalwar kameez: ${kameezShade}, ${kameezCount} × ${formatMetres(kameezMetres)} (${formatPkr(kameezTotal)})`);
    if (waistcoatCount) pieces.push(`Waistcoat: ${waistcoatShade}, ${waistcoatCount} × ${formatMetres(waistcoatMetres)} (${formatPkr(waistcoatTotal)})`);
    return [`Hello Sara Fabrics, please confirm availability for ${quality}${lot ? `, lot ${lot}` : ''}.`,
      ...pieces, `Estimated total: ${formatPkr(total)}. Please confirm current stock and final price.`].join('\n');
  };

  const MAX_LIST_LINES = 40;
  const linesFromSelection = ({ kameezShade, waistcoatShade, kameezCount, waistcoatCount,
    kameezMetres, waistcoatMetres, kameezUnit, waistcoatUnit }) => {
    const lines = [];
    if (kameezCount) lines.push({ piece: 'Shalwar kameez', shade: kameezShade, count: kameezCount, metres: kameezMetres, unit: kameezUnit });
    if (waistcoatCount) lines.push({ piece: 'Waistcoat', shade: waistcoatShade, count: waistcoatCount, metres: waistcoatMetres, unit: waistcoatUnit });
    return lines;
  };
  // Same piece, shade and cut length on one line; counts add up, capped at 999 cuts per line.
  const addLines = (list, lines) => {
    const next = list.map((line) => ({ ...line }));
    for (const line of lines) {
      const same = next.find((item) => item.piece === line.piece && item.shade === line.shade && Math.abs(item.metres - line.metres) < 0.000001);
      if (same) {
        if (same.count + line.count > 999) return { ok: false, list, error: 'A single line can hold up to 999 cuts.' };
        same.count += line.count;
        same.unit = line.unit;
      } else {
        if (next.length >= MAX_LIST_LINES) return { ok: false, list, error: `Your list can hold up to ${MAX_LIST_LINES} lines; send it before adding more.` };
        next.push({ ...line });
      }
    }
    return { ok: true, list: next };
  };
  // A saved list is only a hint: keep lines whose piece and shade still exist on this page,
  // and recompute every price from the page's current rate and standard-cut prices.
  const revalidateList = (saved, { rate, kameezStandard, waistcoatStandard, kameezPrice, waistcoatPrice, shades }) => {
    const pieces = { 'Shalwar kameez': [kameezStandard, kameezPrice], Waistcoat: [waistcoatStandard, waistcoatPrice] };
    const valid = [];
    for (const line of saved) {
      if (!line || !Object.prototype.hasOwnProperty.call(pieces, line.piece) || !shades.includes(line.shade)) continue;
      if (!Number.isInteger(line.count) || line.count < 1 || line.count > 999) continue;
      if (!Number.isFinite(line.metres) || line.metres < 0.01 || line.metres > 100 || Math.abs(line.metres * 100 - Math.round(line.metres * 100)) > 0.000001) continue;
      const [standard, standardPrice] = pieces[line.piece];
      valid.push({ piece: line.piece, shade: line.shade, count: line.count, metres: line.metres, unit: priceForCut(rate, line.metres, standard, standardPrice) });
      if (valid.length >= MAX_LIST_LINES) break;
    }
    return valid;
  };
  const listTotal = (list) => list.reduce((sum, line) => sum + line.unit * line.count, 0);
  const describeLine = (line) => `${line.piece}: ${line.shade}, ${line.count} × ${formatMetres(line.metres)} (${formatPkr(line.unit * line.count)})`;
  const buildListMessage = ({ quality, lot, list }) => [
    `Hello Sara Fabrics, please confirm availability for ${quality}${lot ? `, lot ${lot}` : ''}.`,
    ...list.map((line, index) => `${index + 1}. ${describeLine(line)}`),
    `Estimated total: ${formatPkr(listTotal(list))}. Please confirm current stock and final price.`,
  ].join('\n');

  if (typeof module !== 'undefined' && module.exports) module.exports = { quote, priceForCut, buildMessage, linesFromSelection, addLines, listTotal, buildListMessage, revalidateList, MAX_LIST_LINES };
  if (typeof document === 'undefined') return;

  document.querySelectorAll('[data-sf-order-builder]').forEach((root) => {
    const rate = Number(root.dataset.rate);
    const kameezStandard = Number(root.dataset.kameezStandard);
    const waistcoatStandard = Number(root.dataset.waistcoatStandard);
    const kameezPrice = Number(root.dataset.kameezPrice);
    const waistcoatPrice = Number(root.dataset.waistcoatPrice);
    const base = root.dataset.whatsappBase;
    const estimate = root.querySelector('[data-sf-estimate]');
    const error = root.querySelector('[data-sf-error]');
    const link = root.querySelector('[data-sf-enquire]');
    const addButton = root.querySelector('[data-sf-add]');
    const added = root.querySelector('[data-sf-added]');
    const listWrap = root.querySelector('[data-sf-list-wrap]');
    const listEl = root.querySelector('[data-sf-list]');
    const listTotalEl = root.querySelector('[data-sf-list-total]');
    const storageKey = `sf-lot-list:${root.dataset.quality}:${root.dataset.lot || 'no-lot'}`;
    const loadList = () => {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        return Array.isArray(saved) ? revalidateList(saved, {
          rate, kameezStandard, waistcoatStandard, kameezPrice, waistcoatPrice,
          shades: [...new Set([...root.querySelectorAll('[data-sf-shade-choice]')].map((b) => b.dataset.shade))],
        }) : [];
      } catch (e) { return []; }
    };
    const saveList = () => {
      try { window.localStorage.setItem(storageKey, JSON.stringify(list)); } catch (e) { /* private mode: list lives for this visit only */ }
    };
    let list = loadList();
    let current = null;
    if (addButton) addButton.hidden = false;

    const selectedShade = (piece) => root.querySelector(`[data-sf-shade-group="${piece}"] [data-sf-shade-choice][aria-pressed="true"]`)?.dataset.shade || '';
    const selectedLength = (piece, standard) => {
      const custom = root.querySelector(`[data-sf-custom-toggle="${piece}"]`)?.checked;
      const field = root.querySelector(`[data-sf-length="${piece}"]`);
      return custom && field ? Number(field.value) : standard;
    };
    const selectedCount = (piece) => Number(root.querySelector(`[data-sf-count="${piece}"]`)?.value);

    const showSelection = (piece) => {
      const card = root.querySelector(`[data-sf-selection="${piece}"]`);
      const chosen = root.querySelector(`[data-sf-shade-group="${piece}"] [data-sf-shade-choice][aria-pressed="true"]`);
      if (!card || !chosen) return;
      const image = card.querySelector('[data-sf-selection-image]');
      const name = card.querySelector('[data-sf-selection-name]');
      const label = piece === 'shalwar kameez' ? 'shalwar kameez' : 'waistcoat';
      if (image && chosen.dataset.thumb) { image.src = chosen.dataset.thumb; image.alt = `Selected ${label} fabric: ${chosen.dataset.shade}`; }
      if (name) name.textContent = `${root.dataset.quality} — ${chosen.dataset.shade}`;
      card.classList.toggle('is-unused', selectedCount(piece) === 0);
    };

    const renderList = () => {
      listEl.replaceChildren(...list.map((line, index) => {
        const item = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = `${index + 1}. ${describeLine(line)}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'sf-lot-page__remove';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${line.piece} ${line.shade} from your list`);
        remove.addEventListener('click', () => { list.splice(index, 1); saveList(); render(); });
        item.append(text, remove);
        return item;
      }));
      listWrap.hidden = list.length === 0;
      listTotalEl.textContent = list.length ? `List total: ${formatPkr(listTotal(list))}` : '';
    };

    const render = () => {
      const kameezCount = selectedCount('shalwar kameez');
      const waistcoatCount = selectedCount('waistcoat');
      const kameezMetres = selectedLength('shalwar kameez', kameezStandard);
      const waistcoatMetres = selectedLength('waistcoat', waistcoatStandard);
      const result = quote({ rate, kameezCount, waistcoatCount, kameezMetres, waistcoatMetres,
        kameezStandard, waistcoatStandard, kameezPrice, waistcoatPrice });
      showSelection('shalwar kameez');
      showSelection('waistcoat');
      renderList();
      current = null;
      if (!result.valid) {
        estimate.textContent = '';
        error.textContent = result.error;
        error.hidden = false;
        if (addButton) addButton.disabled = true;
        if (list.length) { setLink(buildListMessage({ quality: root.dataset.quality, lot: root.dataset.lot, list }), list.length); return; }
        link.setAttribute('aria-disabled', 'true');
        link.removeAttribute('href');
        link.setAttribute('tabindex', '-1');
        return;
      }

      error.textContent = '';
      error.hidden = true;
      estimate.textContent = `Estimated fabric total: ${formatPkr(result.total)}`;
      if (addButton) addButton.disabled = false;
      current = linesFromSelection({ kameezShade: selectedShade('shalwar kameez'), waistcoatShade: selectedShade('waistcoat'),
        kameezCount, waistcoatCount, kameezMetres, waistcoatMetres, kameezUnit: result.kameezUnit, waistcoatUnit: result.waistcoatUnit });
      if (list.length) {
        setLink(buildListMessage({ quality: root.dataset.quality, lot: root.dataset.lot, list }), list.length);
      } else {
        setLink(buildMessage({ quality: root.dataset.quality, lot: root.dataset.lot,
          kameezShade: selectedShade('shalwar kameez'), waistcoatShade: selectedShade('waistcoat'),
          kameezCount, waistcoatCount, kameezMetres, waistcoatMetres,
          kameezTotal: result.kameezTotal, waistcoatTotal: result.waistcoatTotal, total: result.total }), 0);
      }
    };

    function setLink(message, listLength) {
      const url = new URL(base);
      url.searchParams.set('text', message);
      link.href = url.toString();
      link.removeAttribute('tabindex');
      link.setAttribute('aria-disabled', 'false');
      link.textContent = listLength ? `Send your list (${listLength} ${listLength === 1 ? 'line' : 'lines'}) to sales on WhatsApp` : 'Send this selection to sales on WhatsApp';
    }

    if (addButton) addButton.addEventListener('click', () => {
      if (!current || !current.length) return;
      const outcome = addLines(list, current);
      if (!outcome.ok) { added.textContent = outcome.error; return; }
      list = outcome.list;
      saveList();
      added.textContent = `Added to your list: ${current.map(describeLine).join('; ')}. Choose more shades or quantities, or send the list.`;
      render();
    });
    root.querySelector('[data-sf-clear]')?.addEventListener('click', () => {
      list = [];
      saveList();
      added.textContent = 'Your list is cleared.';
      render();
    });

    root.querySelectorAll('[data-sf-shade-group]').forEach((group) => {
      group.querySelectorAll('[data-sf-shade-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('[data-sf-shade-choice]').forEach((option) => option.setAttribute('aria-pressed', String(option === button)));
          render();
        });
      });
    });
    root.querySelectorAll('[data-sf-count], [data-sf-length]').forEach((input) => input.addEventListener('input', render));
    root.querySelectorAll('[data-sf-custom-toggle]').forEach((toggle) => {
      toggle.addEventListener('change', () => {
        const length = root.querySelector(`[data-sf-length="${toggle.dataset.sfCustomToggle}"]`);
        const label = length?.closest('.sf-lot-page__custom-length');
        if (length) length.disabled = !toggle.checked;
        if (label) label.hidden = !toggle.checked;
        render();
      });
    });
    render();
  });
})();
